import { AddIcon } from '@chakra-ui/icons';
import {
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    IconButton,
    Tooltip,
} from '@chakra-ui/react';
import { cloneDeep, isEmpty, sortBy } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useCurrentColors } from '../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';
import Menu from './Menu';

const filterSubmenu = (menuStructure: any, submenuList: any) => {
    let list = submenuList;
    menuStructure.forEach((m: any) => {
        list = list.filter((o: any) => o.key !== m.key);
        if (m.sub_menu) {
            list = filterSubmenu(m.sub_menu, list);
        }
    });
    return list;
};

export const ItemTypes = {
    CARD: 'card',
};

export type MenuType = {
    key: string;
    menu_id: number;
    name: string;
    path: string;
    sub_menu: MenuType[];
    type: string;
};

type MenuGroupProps = {
    key: string;
    app?: any;
    title: string;
    menuKey: string;
    allMenus?: MenuType[];
    menuStructure?: MenuType[];
    titleTranslation: string;
    t: any;
    edit: boolean;
    totalDepth?: number;
    change: any;
    disabled?: boolean;
    index: number;
    expandOnAdd: (index: number) => void;
};

const MenuGroup = ({
    title,
    app,
    allMenus,
    menuStructure,
    menuKey,
    titleTranslation,
    t,
    edit,
    totalDepth,
    change,
    disabled,
    index,
    expandOnAdd,
}: MenuGroupProps) => {
    const level = 0;
    const currentColors = useCurrentColors();
    const [menus, setMenus] = useState<any>([]);
    const [submenuOptions, setSubmenuOptions] = useState<any>([]);
    const [options, setOptions] = useState<any>([]);

    useEffect(() => {
        let list = allMenus?.filter((o: any) => o.type === menuKey);
        if (menuStructure && list) {
            list = list.filter(
                (o: any) =>
                    !menuStructure?.find((m: any) => m.menu_id === o.menu_id)
            );
        }
        setOptions(sortBy(list, 'name'));

        let subList = allMenus?.filter((o: any) => o.type === 'submenu');
        if (menuStructure && subList) {
            subList = filterSubmenu(menuStructure, subList);
        }
        setSubmenuOptions(sortBy(subList, 'name'));
    }, [allMenus, menuKey, menuStructure]);

    useEffect(() => {
        if (edit) {
            change(menuKey, menus);
        }
    }, [menuKey, menus, change, edit]);

    useEffect(() => {
        if (menuStructure) {
            const list =
                menuStructure.filter((item: any) => item?.type === menuKey) ||
                [];
            setMenus(list || []);
        } else {
            setMenus([]);
        }
    }, [menuStructure, menuKey, edit]);

    const moveCard = useCallback(
        (dragIndex: number, hoverIndex: number, item: any) => {
            const source = menus.findIndex((m: any) => m.key === item.menu.key);
            if (source > -1) {
                const destination = hoverIndex;

                if (destination > -1) {
                    const list = cloneDeep(menus);
                    const sourceItem = list[source];

                    if (destination < source) {
                        list.splice(destination, 0, sourceItem);
                        list.splice(source + 1, 1);
                    } else if (destination > source) {
                        list.splice(destination + 1, 0, sourceItem);
                        list.splice(source, 1);
                    }
                    setMenus(list);
                }
            }
        },
        [menus]
    );

    const updateMenu = useCallback(
        (index: number, menu: any) => {
            if (index === null) {
                index = menus.findIndex((m: any) => m.key === menu.key);
            }

            const updatedMenu = cloneDeep(menus);
            updatedMenu[index] = { ...updatedMenu[index], ...menu };

            setMenus(updatedMenu);
        },
        [menus]
    );

    const addMenu = useCallback(
        (event: any) => {
            event.stopPropagation();
            if (allMenus) {
                setMenus([...cloneDeep(menus), {}]);
                expandOnAdd(index);
            }
        },
        [menus, allMenus, index, expandOnAdd]
    );

    const addSubMenu = useCallback(
        (menu: any, list: any) => {
            const index = menus.findIndex((m: any) => m.key === menu.key);

            const updatedMenu = cloneDeep(menus);
            updatedMenu[index] = { ...updatedMenu[index], sub_menu: list };
            setMenus(updatedMenu);
        },
        [menus]
    );

    const deleteMenu = useCallback(
        (index: number, item: any) => {
            if (edit) {
                if (index === null) {
                    index = menus.findIndex((m: any) => m.key === item.key);
                }
                if (index > -1) {
                    const updatedMenu = menus.filter(
                        (m: any, i: number) => index !== i
                    );
                    setMenus(updatedMenu);
                    updateOptions(item, 'ADD');
                }
            }
        },
        [edit, menus]
    );

    const updateOptions = useCallback(
        (item: any, op: 'ADD' | 'REMOVE', freeItem?: any) => {
            const originalItem = allMenus?.find((m: any) => m.key === item.key);

            if (item.type === 'submenu') {
                if (op === 'REMOVE') {
                    let list = submenuOptions.filter(
                        (m: any) => m.key !== item.key
                    );
                    if (!isEmpty(freeItem)) {
                        list = list.concat(freeItem);
                    }
                    setSubmenuOptions(sortBy(list, 'name'));
                } else {
                    setSubmenuOptions(
                        sortBy([...submenuOptions, originalItem], 'name')
                    );
                }
            } else {
                if (op === 'REMOVE') {
                    let list = options.filter((m: any) => m.key !== item.key);
                    if (!isEmpty(freeItem)) {
                        list = list.concat(freeItem);
                    }
                    setOptions(sortBy(list, 'name'));
                } else {
                    setOptions(sortBy([...options, originalItem], 'name'));
                }
            }
        },
        [options, submenuOptions]
    );

    useEffect(() => {
        if (!menuStructure) {
            setMenus([]);
        }
    }, [app]);
    return (
        <AccordionItem
            border={0}
            my={4}>
            <AccordionButton
                borderRadius={4}
                backgroundColor={currentColors.brand['800']}
                _hover={{
                    backgroundColor: currentColors.brand['700'],
                }}>
                <AccordionIcon />
                <Box
                    flex="1"
                    textAlign="left">
                    {t(`ui.mm.menu-mapping.${title}`)}
                </Box>
                {edit && (
                    <Tooltip
                        aria-label="add-menu"
                        label={t('ui.mm.tooltip.add-menu')}
                        hasArrow>
                        <IconButton
                            background="transparent"
                            cursor="pointer"
                            border="none"
                            icon={
                                <AddIcon
                                    width={'14px !important'}
                                    height={'14px !important'}
                                />
                            }
                            aria-label="add-menu"
                            variant="ghost"
                            color={currentColors.brand['500']}
                            _hover={{
                                backgroundColor: 'transparent',
                                color: currentColors.brand['400'],
                            }}
                            _focus={{
                                border: '0px',
                                backgroundColor: 'transparent',
                                outline: 'none',
                            }}
                            _active={{ border: '0px' }}
                            onClick={addMenu}
                            disabled={disabled}
                        />
                    </Tooltip>
                )}
            </AccordionButton>
            <AccordionPanel
                paddingY={4}
                pl={8}
                pr={0}>
                <DndProvider backend={HTML5Backend}>
                    {menus?.map((item: any, index: number) => {
                        return (
                            <Menu
                                key={`${item?.key}_${level}_${index}`}
                                menu={item}
                                app={app}
                                index={index}
                                currentColors={currentColors}
                                allMenus={allMenus}
                                titleTranslation={titleTranslation}
                                t={t}
                                paddingY={2}
                                px={4}
                                mb={4}
                                borderRadius={4}
                                borderWidth={1}
                                borderColor={currentColors.componentBorder}
                                backgroundColor={currentColors.brand['900']}
                                _hover={{
                                    backgroundColor: currentColors.brand['800'],
                                }}
                                edit={edit}
                                totalDepth={totalDepth}
                                level={level + 1}
                                options={options}
                                submenuOptions={submenuOptions}
                                updateMenu={updateMenu}
                                addSubMenu={addSubMenu}
                                moveCard={moveCard}
                                deleteMenu={deleteMenu}
                                updateOptions={updateOptions}
                                disabled={disabled}
                            />
                        );
                    })}
                </DndProvider>
            </AccordionPanel>
        </AccordionItem>
    );
};

export default MenuGroup;
